/**
 * Server-side Jobs API für getStaticProps / getStaticPaths
 * Lädt Stellenangebote vom CMS (für statisches Rendering / SEO)
 */

import https from 'https';

const CMS_URL = 'https://cms.simplify-data.de';

function generateSlug(title, id = null) {
    if (!title) return id ? String(id) : '';
    const slug = title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    return id ? `${slug}-${id}` : slug;
}

function httpsGet(url) {
    return new Promise((resolve) => {
        const request = https.get(url, { timeout: 10000 }, (response) => {
            if (response.statusCode !== 200) {
                resolve({ data: [] });
                return;
            }
            let data = '';
            response.on('data', (chunk) => { data += chunk; });
            response.on('end', () => {
                try {
                    resolve(JSON.parse(data));
                } catch {
                    resolve({ data: [] });
                }
            });
        });
        request.on('error', () => resolve({ data: [] }));
        request.on('timeout', () => {
            request.destroy();
            resolve({ data: [] });
        });
    });
}

/**
 * Lädt alle Jobs vom CMS (unverändert, nutzbar für KarriereJobsTabs)
 */
export async function fetchJobs() {
    const result = await httpsGet(`${CMS_URL}/items/jobs?fields=*,translations.*`);
    return result.data || [];
}

/**
 * Lädt aktive Jobs (enabled, nicht abgelaufen)
 */
export async function fetchActiveJobs() {
    const jobs = await fetchJobs();
    const now = new Date();
    return jobs.filter((job) => {
        if (!job.enabled) return false;
        if (job.validTill && new Date(job.validTill) < now) return false;
        return true;
    });
}

/**
 * Slug für Job (DE oder EN Titel)
 */
export function getJobSlug(job, locale = 'de') {
    const langCode = locale === 'de' ? 'de-DE' : 'en-US';
    const translation = job.translations?.find((t) => t.languages_code === langCode) || job.translations?.[0];
    const title = translation?.title || translation?.Titel || 'Job';
    return generateSlug(title, job.id);
}

/**
 * Job nach Slug finden (Slug endet mit -{id})
 * Findet Job per ID; Slug dient nur zur Validierung.
 */
export async function fetchJobBySlug(slug, locale = 'de') {
    const jobs = await fetchJobs();
    const jobId = slug?.split('-').pop();
    const job = jobs.find((j) => String(j.id) === String(jobId));
    return job || null;
}
